import React, { useState } from 'react';
import { Link,  Alert, Snackbar, IconButton,Grid, Box, Button, TextField, Typography, Checkbox, FormControlLabel, Paper, Stack, CircularProgress } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CopyAll, InfoOutlined } from '@mui/icons-material';
import ShareButton from './ShareButton';

const CreateSecret = () => {
    const [secret, setSecret] = useState('');
    const [maxReadCount, setMaxReadCount] = useState(2); // default
    const [validUntil, setValidUntil] = useState(null);  // optional Date
    const [validAt, setValidAt] = useState(null);  // optional Date
    const [requireEmailAuth, setRequireEmailAuth] = useState(false);
    const [authEmail, setAuthEmail] = useState('');
    const [secretLink, setSecretLink] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [linkCreated, setLinkCreated] = useState(false);
    const [burnAfterRead, setBurnAfterRead] = useState(0);
    const [oneTime, setOneTime] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false); // Close snackbar
      };

    const encryptSecret = async (secret) => {
      try {
          const encoder = new TextEncoder();
          const data = encoder.encode(secret);
          // 256-bit random key used directly (full entropy, no KDF needed).
          const keyBytes = crypto.getRandomValues(new Uint8Array(32));
          // 96-bit nonce, the recommended IV size for AES-GCM.
          const iv = crypto.getRandomValues(new Uint8Array(12));

          const key = await crypto.subtle.importKey(
            'raw',
            keyBytes,
            { name: 'AES-GCM' },
            false,
            ['encrypt']
          );

          // AES-GCM is authenticated: the 128-bit auth tag is appended to the
          // ciphertext, so tampering is detected on decryption.
          const ciphertextBuffer = await crypto.subtle.encrypt(
            {
              name: 'AES-GCM',
              iv: iv,
            },
            key,
            data
          );

          // Base64 helper
          const toBase64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));

          return {
            encryptedSecret: toBase64(ciphertextBuffer),
            iv: toBase64(iv),
            encrpytKey: toBase64(keyBytes),
          };
      } catch (error) {
        console.error('Error encrypting secret:', error);
        return null;
      }

    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(secretLink) // Write the link to the clipboard
            .then(() => {
                setSnackbarOpen(true); // Show snackbar on success
            })
            .catch(err => {
                console.error('Failed to copy the link: ', err);
            });
    };

    const handleCreateSecretLink = async () => {
        if (!secret) {
            alert('Please enter a secret.');
            return;
        }
        if (maxReadCount < 1) {
            alert('Maximum read count must be at least 1.');
            return;
        }
        if (validUntil && new Date(validUntil) <= new Date()) {
            alert('Valid until date must be in the future.');
            return;
        }

        const trimmedAuthEmail = authEmail.trim();
        if (requireEmailAuth && trimmedAuthEmail &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedAuthEmail)) {
            alert('Please enter a valid allowed email address.');
            return;
        }

        setIsLoading(true);

        const encrypted = await encryptSecret(secret);
        if (!encrypted) {
            alert('Failed to encrypt the secret. Please try again.');
            setIsLoading(false);
            return;
        }
        const { encryptedSecret, encrpytKey, iv } = encrypted;

        const functions = getFunctions();
        const createSecret = httpsCallable(functions, 'createSecret');
      
        try {
          const result = await createSecret({
            encryptedSecret,
            maxReadCount,
            validUntil,
            validAt,
            requireEmailAuth,
            authEmail: requireEmailAuth ? trimmedAuthEmail : '',
            currentReadCount: 0,
            burnAfterRead,
            oneTime,
            iv: iv,
          });

        if (result.data.success) {
            setSecretLink("https://share-a-secret.com/view/" + result.data.id + "#k=" + encrpytKey);
            setLinkCreated(true);
          } else {
            console.error("Failed saving the secret. ", result.data.message);
            alert(result.data.message || 'Failed to create the secret. Please try again.');
          }
        } catch (error) {
          console.error("Fehler beim Aufruf der Function:", error);
          
        }
        setIsLoading(false);
    }

    return (
<Box>
    {isLoading ? (
        <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh', // or any height you need
        }}
      >
        <CircularProgress />
      </Box>
    ) : linkCreated ? (
        <Paper elevation={0} variant="outlined" sx={{ p: 3, width: '100%', margin: 'auto' }}>
            <Typography variant="h6" gutterBottom>
                Secret Created Successfully!
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                Your secret has been encrypted and stored securely. The link below allows you to access it. Please note that the secret will be deleted after the specified read count or time limit.
            </Typography>
            {/* Displaying the Secret Link with a Copy Button */}
            <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                    label="Secret Link"
                    fullWidth
                    value={secretLink} // The secret link generated
                    InputProps={{
                        readOnly: true, // Make the input field readonly since it's just to show the link
                    }}
                />
                <IconButton color="primary" onClick={handleCopyLink}>
                    <CopyAll />
                </IconButton>
                <ShareButton secretLink={secretLink} />
            </Stack>

            {/* Snackbar component */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000} // Snackbar will close automatically after 3 seconds
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity="success">
                Secret link copied to clipboard!
                </Alert>
            </Snackbar>

            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Secrets can only be decrypted with the key provided in the link. Make sure to share it securely.
            </Typography>

            <Button variant="contained" color="primary" onClick={() => setLinkCreated(false)} sx={{ mt: 3 }}>
                Create Another Secret
            </Button>
        </Paper>
    ) : (
        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, sm: 4 }, width: '100%', margin: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
        Create a Secret
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your secret is encrypted in your browser before it is sent. Configure how long it stays available below.
      </Typography>

      {/* Secret Input */}
      <TextField
        label="Your Secret you want to share"
        multiline
        rows={5}
        fullWidth
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
        sx={{ mb: 2 }}
        inputProps={{
          maxLength: 500, // Limit to 500 characters
        }}
      />
        <Typography variant="caption" color="textSecondary" sx={{ mb: 3 }}>
            {secret.length} / 500
        </Typography>

      {/* Options underneath */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Maximum Read Count"
            type="number"
            fullWidth
            value={maxReadCount}
            onChange={(e) => setMaxReadCount(e.target.value)}
            InputProps={{
              inputProps: { min: 1 },
              maxLength: 10,
            }}
            disabled={oneTime}
            helperText="The secret is deleted after this many reads."
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Auto reload after reading (in seconds)"
            type="number"
            fullWidth
            value={burnAfterRead}
            onChange={(e) => setBurnAfterRead(e.target.value)}
            InputProps={{
              inputProps: { min: 1 },
            }}
            helperText="Page reloads after this many seconds. Best paired with One Time Secret."
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Valid Until"
            type="datetime-local"
            fullWidth
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              inputProps: { min: new Date().toISOString().slice(0, 16) },
            }}
            helperText="The secret is permanently deleted at this time."
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Valid At"
            type="datetime-local"
            fullWidth
            value={validAt}
            onChange={(e) => setValidAt(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              inputProps: { min: new Date().toISOString().slice(0, 16) },
            }}
            helperText="The secret only becomes accessible from this time."
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={requireEmailAuth}
                onChange={(e) => setRequireEmailAuth(e.target.checked)}
              />
            }
            label="Require Email Authentication"
          />
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlined fontSize="small" sx={{ mr: 0.5 }} />
            The viewer must verify an email address via a link before the secret unlocks.
          </Typography>
            {requireEmailAuth && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Allowed Email (optional)"
                  type="email"
                  fullWidth
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  helperText="If set, only this email can view the secret. Leave empty to allow any verified email."
                />
              </Box>
            )}
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={oneTime}
                onChange={(e) => setOneTime(e.target.checked)}
              />
            }
            label="One Time Secret"
          />
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlined fontSize="small" sx={{ mr: 0.5 }} />
            The secret is deleted immediately after it is viewed once.
          </Typography>
        </Grid>
      </Grid>

      {/* Button centered at bottom */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I accept the {" "}
            <Link href="/terms-of-use" target="_blank" rel="noopener">
              Terms of use
            </Link>
            {" "} and {" "}
             <Link href="/data-privacy" target="_blank" rel="noopener">
              Privacy Policy
            </Link>
            .
          </Typography>
        }
      />
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateSecretLink}
          disabled={!accepted}
          sx={{
            px: 4,
            py: 1.2,
            borderRadius: 2,
            fontWeight: 'bold',
          }}
        >
          Generate Secret Link
        </Button>
      </Box>
    </Paper>
    )}
</Box>

    );
};

export default CreateSecret;
